import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { usePersistentState } from '../../lib/usePersistentState';
import { CloudStorageCamera, CloudStorageCounts, CloudVerdict, CloudRegionsResult } from '../../types';
import { Cloud, RefreshCw, Search, Camera, Power, ChevronRight, ChevronDown, Loader2, Check, Film, Globe, AlertTriangle } from 'lucide-react';

// How many cameras in a set have cloud recording switched OFF (the only ones a
// "turn cloud on" action would actually change).
const cloudOffCount = (cams: CloudStorageCamera[]) => cams.filter((c) => c.cloudMode === 'off').length;

const VERDICTS: { key: CloudVerdict; label: string; short: string; color: string; bg: string }[] = [
  { key: 'working', label: 'Working', short: 'OK', color: 'var(--success-text)', bg: 'rgba(16,185,129,0.12)' },
  { key: 'camera_offline', label: 'Camera offline', short: 'Offline', color: 'var(--danger-text)', bg: 'rgba(239,68,68,0.12)' },
  { key: 'cloud_off', label: 'Cloud off', short: 'Off', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  { key: 'on_but_silent', label: 'On, no recent clips', short: 'Silent', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  { key: 'not_entitled', label: 'Not entitled', short: 'No plan', color: 'var(--text-muted)', bg: 'rgba(148,163,184,0.12)' },
];
const verdictMeta = (v: CloudVerdict) => VERDICTS.find((x) => x.key === v) ?? VERDICTS[4];

function ago(iso?: string | null): string {
  if (!iso) return 'never';
  const d = Date.now() - new Date(iso).getTime();
  const m = Math.floor(d / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const emptyCounts: CloudStorageCounts = {
  working: 0, onButSilent: 0, cloudOff: 0, cameraOffline: 0, notEntitled: 0, total: 0,
};

export const CloudStoragePage: React.FC = () => {
  const [view, setView] = useState<'camera' | 'site'>('camera');
  const [cameras, setCameras] = useState<CloudStorageCamera[]>([]);
  const [counts, setCounts] = useState<CloudStorageCounts>(emptyCounts);
  const [filter, setFilter] = usePersistentState<CloudVerdict | 'all'>('cloudStorage.filter', 'all');
  const [search, setSearch] = usePersistentState('cloudStorage.search', '');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busySerial, setBusySerial] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState<string | null>(null);
  const navigate = useNavigate();

  // Recordings now open on their own screen (deep-linkable), not a modal.
  const openRecordings = useCallback((c: CloudStorageCamera) => {
    const qs = new URLSearchParams({ vuid: c.serialNumber, name: c.name || c.serialNumber });
    navigate(`/recordings?${qs}`);
  }, [navigate]);

  // Cloud region diagnostics (which cameras are on a non-US eye4 region and so
  // can't have their recordings pulled). Loaded once, independent of the filters.
  const [regions, setRegions] = useState<CloudRegionsResult | null>(null);
  const [regionsOpen, setRegionsOpen] = useState(false);
  useEffect(() => {
    api.getCloudRegions().then(setRegions).catch(() => setRegions(null));
  }, []);

  // Site view holds the whole (search-scoped) set so it can group by customer.
  const [siteCams, setSiteCams] = useState<CloudStorageCamera[] | null>(null);
  const [siteLoading, setSiteLoading] = useState(false);
  const [openSites, setOpenSites] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Camera view: server-side verdict + search filtering.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.getCloudStorage(filter === 'all' ? undefined : filter, debounced || undefined, 1, 200);
      setCameras(d.cameras);
      setCounts(d.counts);
    } catch (err) {
      console.error('Failed to load cloud storage', err);
      setError('Could not load cloud-storage health. Check that you are signed in as an admin.');
    } finally {
      setLoading(false);
    }
  }, [filter, debounced]);

  // Site view: load the whole search-scoped set (all verdicts), group client-side.
  const loadSite = useCallback(async () => {
    setSiteLoading(true);
    setError(null);
    try {
      const all: CloudStorageCamera[] = [];
      for (let page = 1; page <= 20; page++) {
        const d = await api.getCloudStorage(undefined, debounced || undefined, page, 100);
        all.push(...d.cameras);
        setCounts(d.counts);
        if (all.length >= d.pagination.totalItems || d.cameras.length === 0) break;
      }
      setSiteCams(all);
    } catch (err) {
      console.error('Failed to load cloud storage sites', err);
      setError('Could not load cloud-storage health.');
      setSiteCams([]);
    } finally {
      setSiteLoading(false);
    }
  }, [debounced]);

  // Invalidate the site cache whenever the search changes.
  useEffect(() => { setSiteCams(null); }, [debounced]);
  useEffect(() => { if (view === 'camera') load(); }, [view, load]);
  useEffect(() => { if (view === 'site' && siteCams === null) loadSite(); }, [view, siteCams, loadSite]);

  const refresh = () => { if (view === 'camera') load(); else { setSiteCams(null); } };

  const flash = (msg: string) => { setNotice(msg); setTimeout(() => setNotice(null), 5000); };

  const turnCloudOn = async (c: CloudStorageCamera) => {
    setBusySerial(c.serialNumber);
    try {
      await api.setCloudMode(c.serialNumber, 'motion');
      refresh();
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to turn cloud on.');
    } finally {
      setBusySerial(null);
    }
  };

  const activateOwner = async (ownerId: string, label: string, count: number) => {
    if (!window.confirm(`Turn cloud recording on for all ${count} of ${label}'s cameras?`)) return;
    setBulkBusy(ownerId);
    setError(null);
    try {
      const { updated } = await api.setCustomerCloudMode(ownerId, 'motion');
      flash(`Cloud recording activated on ${updated} camera${updated === 1 ? '' : 's'} for ${label}.`);
      refresh();
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to activate cloud for the customer.');
    } finally {
      setBulkBusy(null);
    }
  };

  const countFor = (k: CloudVerdict | 'all') =>
    k === 'all' ? counts.total
    : k === 'working' ? counts.working
    : k === 'camera_offline' ? counts.cameraOffline
    : k === 'cloud_off' ? counts.cloudOff
    : k === 'on_but_silent' ? counts.onButSilent
    : counts.notEntitled;

  // Camera-view single-customer bar.
  const owners = Array.from(new Set(cameras.map((c) => c.ownerId)));
  const singleOwner = owners.length === 1 && cameras.length > 0 ? cameras[0] : null;

  return (
    <div>
      <div
        style={{
          background: 'var(--bg-surface-card)', border: '1px solid var(--border-dim)',
          borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <Cloud size={18} color="var(--primary-light)" />
            <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>Cloud Storage Health</h2>
          </div>
          <p style={{ fontSize: '12.5px', color: 'var(--text-muted)', maxWidth: '640px' }}>
            Whether cloud recording is actually working per camera — plan entitlement, cloud switch, camera liveness,
            and recent clips. Use it when a customer with an active plan says cloud isn't working.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', border: '1px solid var(--border-dim)', borderRadius: '6px', overflow: 'hidden' }}>
            <button onClick={() => setView('camera')} style={{ fontSize: '12px', padding: '6px 12px', cursor: 'pointer', border: 'none', background: view === 'camera' ? 'rgba(56,189,248,0.15)' : 'transparent', color: view === 'camera' ? 'var(--primary-light)' : 'var(--text-muted)', fontWeight: view === 'camera' ? 600 : 400 }}>By camera</button>
            <button onClick={() => setView('site')} style={{ fontSize: '12px', padding: '6px 12px', cursor: 'pointer', border: 'none', borderLeft: '1px solid var(--border-dim)', background: view === 'site' ? 'rgba(56,189,248,0.15)' : 'transparent', color: view === 'site' ? 'var(--primary-light)' : 'var(--text-muted)', fontWeight: view === 'site' ? 600 : 400 }}>By site</button>
          </div>
          <button
            onClick={refresh} disabled={loading || siteLoading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', border: '1px solid var(--border-dim)', background: 'transparent', color: 'var(--text-muted)' }}
          >
            <RefreshCw size={13} className={loading || siteLoading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Cloud region issues — cameras on a non-US eye4 region (China clips
          can't be pulled into recordings; EU is now supported). */}
      {regions && regions.affected.length > 0 && (
        <RegionSection data={regions} open={regionsOpen} setOpen={setRegionsOpen} />
      )}

      {/* Verdict filter chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {(['all', 'working', 'camera_offline', 'cloud_off', 'on_but_silent', 'not_entitled'] as const).map((k) => {
          const active = filter === k;
          const label = k === 'all' ? 'All' : verdictMeta(k).label;
          return (
            <button
              key={k}
              onClick={() => setFilter(k)}
              style={{
                fontSize: '12px', padding: '5px 12px', borderRadius: '999px', cursor: 'pointer',
                border: `1px solid ${active ? 'var(--primary-light)' : 'var(--border-dim)'}`,
                background: active ? 'rgba(56,189,248,0.12)' : 'transparent',
                color: active ? 'var(--primary-light)' : 'var(--text-muted)', fontWeight: active ? 600 : 400,
              }}
            >
              {label} ({countFor(k)})
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '14px', maxWidth: '360px' }}>
        <Search size={14} style={{ position: 'absolute', left: '10px', top: '9px', color: 'var(--text-dim)' }} />
        <input
          type="text"
          className="input"
          placeholder="Search customer, email, phone, camera…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: '100%', paddingLeft: '30px', fontSize: '13px' }}
        />
      </div>

      {notice && (
        <div style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.35)', color: 'var(--success-text)', borderRadius: 'var(--radius-lg)', padding: '10px 16px', marginBottom: '16px', fontSize: '13px' }}>{notice}</div>
      )}
      {error && (
        <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)', color: 'var(--danger-text)', borderRadius: 'var(--radius-lg)', padding: '10px 16px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>
      )}

      {view === 'site' ? (
        <SiteView
          cams={siteCams}
          loading={siteLoading}
          filter={filter}
          openSites={openSites}
          setOpenSites={setOpenSites}
          onActivate={activateOwner}
          bulkBusy={bulkBusy}
          onRecordings={openRecordings}
        />
      ) : (
        <>
          {/* Camera-view single-customer activation bar */}
          {singleOwner && (() => {
            const offCount = cloudOffCount(cameras);
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', background: 'rgba(2,132,199,0.08)', border: '1px solid rgba(56,189,248,0.25)', borderRadius: 'var(--radius-lg)', padding: '10px 16px', marginBottom: '14px' }}>
                <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
                  Showing <strong style={{ color: 'var(--text-main)' }}>{singleOwner.ownerName || singleOwner.ownerEmail}</strong> — {cameras.length} camera{cameras.length === 1 ? '' : 's'}
                  {singleOwner.entitled ? '' : ' · plan does not include cloud (activating still sets the policy; recording stays gated until they’re on a cloud plan)'}
                </span>
                {offCount > 0 ? (
                  <button className="btn btn-primary btn-sm" onClick={() => activateOwner(singleOwner.ownerId, singleOwner.ownerName || singleOwner.ownerEmail, cameras.length)} disabled={bulkBusy === singleOwner.ownerId}>
                    <Power size={13} />
                    <span>{bulkBusy === singleOwner.ownerId ? 'Activating…' : `Turn cloud on for ${offCount} camera${offCount === 1 ? '' : 's'}`}</span>
                  </button>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '12.5px', color: 'var(--success-text)' }}>
                    <Check size={14} /> Cloud already on for all cameras
                  </span>
                )}
              </div>
            );
          })()}

          <div className="card" style={{ overflowX: 'auto' }}>
            {loading ? (
              <div style={{ padding: '20px', color: 'var(--text-dim)', fontSize: '13px' }}>Loading…</div>
            ) : cameras.length === 0 ? (
              <div style={{ padding: '20px', color: 'var(--text-dim)', fontSize: '13px' }}>No cameras match.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    <th style={{ padding: '8px 10px' }}>Camera</th>
                    <th style={{ padding: '8px 10px' }}>Customer</th>
                    <th style={{ padding: '8px 10px' }}>Plan</th>
                    <th style={{ padding: '8px 10px' }}>Verdict</th>
                    <th style={{ padding: '8px 10px' }}>Cloud</th>
                    <th style={{ padding: '8px 10px' }}>Last clip</th>
                    <th style={{ padding: '8px 10px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {cameras.map((c) => <CameraRow key={c.id} c={c} busySerial={busySerial} onTurnOn={turnCloudOn} onRecordings={openRecordings} />)}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

    </div>
  );
};

const CameraRow: React.FC<{ c: CloudStorageCamera; busySerial: string | null; onTurnOn: (c: CloudStorageCamera) => void; onRecordings: (c: CloudStorageCamera) => void }> = ({ c, busySerial, onTurnOn, onRecordings }) => {
  const m = verdictMeta(c.verdict);
  return (
    <tr style={{ borderTop: '1px solid var(--border-subtle)' }}>
      <td style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Camera size={13} color="var(--text-dim)" />
          <div>
            <div style={{ color: 'var(--text-main)' }}>{c.name || c.serialNumber}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{c.serialNumber}</div>
          </div>
        </div>
      </td>
      <td style={{ padding: '8px 10px' }}>
        <div style={{ color: 'var(--text-main)' }}>{c.ownerName || c.ownerEmail}</div>
        <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{c.ownerEmail}{c.ownerPhone ? ` • ${c.ownerPhone}` : ''}</div>
      </td>
      <td style={{ padding: '8px 10px' }}>
        <div style={{ color: 'var(--text-main)' }}>{c.planName || '—'}</div>
        <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{c.subscriptionStatus || 'no subscription'}</div>
      </td>
      <td style={{ padding: '8px 10px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: m.color, background: m.bg, border: `1px solid ${m.color}40`, padding: '2px 8px', borderRadius: '999px', whiteSpace: 'nowrap' }}>{m.label}</span>
      </td>
      <td style={{ padding: '8px 10px', color: 'var(--text-muted)' }}>
        {c.cloudMode}{c.online ? '' : ' · offline'}{c.eye4State ? ` · ${c.eye4State}` : ''}
      </td>
      <td style={{ padding: '8px 10px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
        {ago(c.lastClipAt)}{c.clips7d ? ` · ${c.clips7d}/7d` : ''}
      </td>
      <td style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => onRecordings(c)} title="View this camera's cloud recordings">
            <Film size={12} />
            <span>Recordings</span>
          </button>
          {c.verdict === 'cloud_off' && (
            <button className="btn btn-secondary btn-sm" onClick={() => onTurnOn(c)} disabled={busySerial === c.serialNumber}>
              <Power size={12} />
              <span>{busySerial === c.serialNumber ? '…' : 'Turn cloud on'}</span>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// Colour for a region badge: green when we can pull its video, red for China
// (no video at all), amber for anything unknown.
const regionColor = (recordable: boolean, region: string) =>
  recordable
    ? { color: 'var(--success-text)', bg: 'rgba(16,185,129,0.12)' }
    : region === 'cn-hangzhou'
      ? { color: 'var(--danger-text)', bg: 'rgba(239,68,68,0.12)' }
      : { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' };

// The "cloud region issues" section: a region summary plus the expandable list of
// cameras on a non-US region — the ones an operator can't fix with a toggle.
const RegionSection: React.FC<{
  data: CloudRegionsResult;
  open: boolean;
  setOpen: (v: boolean) => void;
}> = ({ data, open, setOpen }) => {
  const affected = data.affected.length;
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16, borderColor: 'rgba(245,158,11,0.35)' }}>
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
          <Globe size={18} color="#f59e0b" style={{ marginTop: 2, flexShrink: 0 }} />
          <div>
            <strong style={{ fontSize: 13.5, color: 'var(--text-main)' }}>Cloud region issues</strong>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 660, marginTop: 2 }}>
              Each camera's clips live on an eye4 regional node (set in the camera's firmware, not by us). We can pull video from <strong>US</strong> and <strong>EU</strong>, but <strong>China (cn-hangzhou) returns no video</strong> — those cameras show alerts but never a playable recording, and no toggle fixes it. They need re-provisioning to a supported region.
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {data.summary.map((s) => {
                const col = regionColor(s.recordable, s.region);
                return (
                  <span key={s.digit} style={{ background: col.bg, border: `1px solid ${col.color}40`, color: col.color, borderRadius: 999, padding: '2px 10px', fontSize: 11.5, fontWeight: 600 }}>
                    {s.regionLabel} {s.cameras}{s.recordable ? '' : ' · no video'}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
        <button
          onClick={() => setOpen(!open)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--border-dim)', background: 'transparent', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {affected} affected camera{affected === 1 ? '' : 's'}
        </button>
      </div>
      {open && (
        <div style={{ borderTop: '1px solid var(--border-subtle)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                <th style={{ padding: '8px 12px' }}>Camera</th>
                <th style={{ padding: '8px 12px' }}>Customer</th>
                <th style={{ padding: '8px 12px' }}>Region</th>
                <th style={{ padding: '8px 12px' }}>Recordings</th>
                <th style={{ padding: '8px 12px' }}>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {data.affected.map((c) => {
                const col = regionColor(c.recordable, c.region);
                return (
                  <tr key={c.serial} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ color: 'var(--text-main)' }}>{c.name || c.serial}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{c.serial}</div>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ color: 'var(--text-main)' }}>{c.ownerName || c.ownerEmail}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{c.ownerEmail}</div>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ background: col.bg, border: `1px solid ${col.color}40`, color: col.color, borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{c.regionLabel}</span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {c.recordable ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--success-text)' }}><Check size={13} /> Supported</span>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--danger-text)' }}><AlertTriangle size={13} /> No cloud video</span>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{ago(c.lastEventAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Group cameras by customer (site), most-troubled first. Each site shows a chip
// per verdict and a one-click "turn cloud on for all". The verdict filter narrows
// which cameras show within each site.
const SiteView: React.FC<{
  cams: CloudStorageCamera[] | null;
  loading: boolean;
  filter: CloudVerdict | 'all';
  openSites: Record<string, boolean>;
  setOpenSites: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  onActivate: (ownerId: string, label: string, count: number) => void;
  bulkBusy: string | null;
  onRecordings: (c: CloudStorageCamera) => void;
}> = ({ cams, loading, filter, openSites, setOpenSites, onActivate, bulkBusy, onRecordings }) => {
  if (loading || cams === null) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={22} className="spin" /></div>;
  }
  const filtered = filter === 'all' ? cams : cams.filter((c) => c.verdict === filter);
  const byOwner = new Map<string, { id: string; name: string; email: string; cams: CloudStorageCamera[] }>();
  for (const c of filtered) {
    const key = c.ownerId || c.ownerEmail || '—';
    if (!byOwner.has(key)) byOwner.set(key, { id: c.ownerId, name: c.ownerName || c.ownerEmail || '(unknown)', email: c.ownerEmail || '', cams: [] });
    byOwner.get(key)!.cams.push(c);
  }
  // Worst first: cameras that aren't "working" bubble up.
  const trouble = (list: CloudStorageCamera[]) => list.filter((c) => c.verdict !== 'working').length;
  const sites = Array.from(byOwner.values()).sort((a, b) => trouble(b.cams) - trouble(a.cams) || b.cams.length - a.cams.length);

  if (sites.length === 0) {
    return <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No cameras match your filter.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {sites.map((site) => {
        const open = !!openSites[site.id];
        const byVerdict = new Map<CloudVerdict, number>();
        site.cams.forEach((c) => byVerdict.set(c.verdict, (byVerdict.get(c.verdict) ?? 0) + 1));
        const anyEntitled = site.cams.some((c) => c.entitled);
        const offCount = cloudOffCount(site.cams);
        return (
          <div key={site.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              onClick={() => setOpenSites((p) => ({ ...p, [site.id]: !p[site.id] }))}
              style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 12, flexWrap: 'wrap' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                {open ? <ChevronDown size={16} color="var(--text-dim)" /> : <ChevronRight size={16} color="var(--text-dim)" />}
                <div style={{ minWidth: 0 }}>
                  <strong style={{ fontSize: 14, color: 'var(--text-main)' }}>{site.name}</strong>
                  <div style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{site.email}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {VERDICTS.filter((v) => byVerdict.get(v.key)).map((v) => (
                  <span key={v.key} style={{ background: v.bg, border: `1px solid ${v.color}40`, color: v.color, borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                    {v.short} {byVerdict.get(v.key)}
                  </span>
                ))}
                <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>{site.cams.length} cams</span>
                {offCount > 0 ? (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => { e.stopPropagation(); onActivate(site.id, site.name, site.cams.length); }}
                    disabled={bulkBusy === site.id}
                    title={anyEntitled ? `Turn cloud on for ${offCount} camera${offCount === 1 ? '' : 's'} with it off` : 'Sets the policy; recording stays gated until they are on a cloud plan'}
                  >
                    <Power size={12} />
                    <span>{bulkBusy === site.id ? 'Activating…' : `Turn cloud on${offCount > 1 ? ` (${offCount})` : ''}`}</span>
                  </button>
                ) : (
                  <span title="Cloud recording is already on for all this site's cameras" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--success-text)' }}>
                    <Check size={13} /> Cloud on
                  </span>
                )}
              </div>
            </div>
            {open && (
              <div style={{ borderTop: '1px solid var(--border-subtle)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                  <tbody>
                    {site.cams.map((c) => {
                      const m = verdictMeta(c.verdict);
                      return (
                        <tr key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                          <td style={{ padding: '8px 12px' }}>
                            <div style={{ color: 'var(--text-main)' }}>{c.name || c.serialNumber}</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{c.serialNumber}</div>
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 600, color: m.color, background: m.bg, border: `1px solid ${m.color}40`, padding: '2px 8px', borderRadius: '999px', whiteSpace: 'nowrap' }}>{m.label}</span>
                          </td>
                          <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>{c.cloudMode}{c.online ? '' : ' · offline'}</td>
                          <td style={{ padding: '8px 12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{ago(c.lastClipAt)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => onRecordings(c)} title="View this camera's cloud recordings">
                              <Film size={12} /><span>Recordings</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
